import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Button, Col, Divider, List, Modal, Row, Segmented,
} from 'antd';
import clsx from 'clsx';
import { sectionsDE, sectionsEN } from './sections';

type LangType = 'en' | 'de';

interface TermsOfServiceProps{
  handleOpen?: (modalOpen: boolean) => void,
  showFooter?: boolean,
}
const LIST_MAX_HEIGHT = 'min(calc(80vh - 106px), 600px)';

const isSubItem = (title: string) => title.includes('.');

function TermsOfServiceModal({ handleOpen, showFooter = true }: TermsOfServiceProps) {
  const [open, setOpen] = useState(true);
  const [agreeEnabled, setAgreeEnabled] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const [language, setLanguage] = useState<LangType>('de');
  const changeLanguage = (lang: LangType) => {
    setLanguage(lang);
  };

  const handleScroll = () => {
    const content = contentRef.current;

    if (content) {
      const scrollPosition = content.scrollHeight - content.scrollTop;
      const { clientHeight } = content;

      // Check if the user is within a gap of ±5 pixels from the bottom
      if (Math.abs(scrollPosition - clientHeight) <= 5) {
        setAgreeEnabled(true);
      }
    }
  };

  useEffect(() => {
    const content = contentRef.current;

    if (content) {
      content.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (content) {
        content.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const sectionsItems = useMemo(() => (
    language === 'de' ? sectionsDE : sectionsEN
  ), [language]);

  return (
    <Modal
      title={(
        <div style={{
          display: 'flex', justifyContent: 'left', alignItems: 'center', gap: '16px',
        }}
        >
          <span>
            Terms of Service
          </span>
          <Segmented
            size="middle"
            shape="round"
            options={[
              { label: 'DE', value: 'de', icon: '🇩🇪' },
              { label: 'EN', value: 'en', icon: '🇺🇸' },
            ]}
            value={language}
            onChange={changeLanguage}
          />
        </div>
      )}
      open={open}
      centered
      closable={!showFooter}
      maskClosable={false}
      width={952}
      footer={
        showFooter ? [
          <Divider key="divider" />,
          <span key="footer-text" style={{ marginRight: '24px' }}>
            By clicking “Agree and Continue”, you agree to our Terms of Service
          </span>,
          <Button
            key="agree"
            type="primary"
            disabled={!agreeEnabled}
            onClick={() => {
              setOpen(false);
            }}
          >
            Agree and Continue
          </Button>,
        ] : []
    }
      onCancel={() => {
        setOpen(false);
        handleOpen?.(false);
      }}
    >
      <Row gutter={16}>
        <Col span={8}>
          <div style={{ maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto', padding: '0 4px 0 0' }}>
            <List
              dataSource={sectionsItems}
              renderItem={({ title, number }, index) => (
                <List.Item>
                  <a
                    href={`#section-${index}`}
                    className={clsx('truncate3', {
                      'start-padding': isSubItem(number),
                    })}
                  >
                    {/* {`${index + 1}. ${title}`} */}
                    {`${number}. ${title}`}
                  </a>
                </List.Item>
              )}
            />
          </div>
        </Col>

        <Col span={16}>
          <div
            ref={contentRef}
            style={{
              minHeight: '200px', maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto', padding: '0 16px',
            }}
          >

            {sectionsItems.map(({
              title, number, listItems, renderContent,
            }, index) => (
              <div key={crypto.randomUUID()} id={`section-${index}`}>
                <h3>
                  {`${number}. ${title}`}
                </h3>
                <p>
                  {renderContent(listItems)}
                </p>
              </div>
            ))}

            <p>
              Sissach, Mai 16, 2025
            </p>
          </div>
        </Col>
      </Row>
    </Modal>
  );
}

export default TermsOfServiceModal;

export function TermsOfServiceButton({
  variant = 'button',
  text = 'Terms of Service',
  modalProps,
}: { text?: string, variant?: 'button' | 'link', modalProps?: TermsOfServiceProps }) {
  const [isModalOpen, setModalOpen] = useState(false);
  const handleClick = () => {
    setModalOpen(true);
  };

  const handleCloseModal = (isOpen: boolean) => {
    setModalOpen(isOpen);
  };

  return (
    <>
      <Button onClick={handleClick} {...(variant === 'link' ? { type: 'link', size: 'small' } : {})}>
        {text}
      </Button>
      {isModalOpen && <TermsOfServiceModal showFooter={false} handleOpen={handleCloseModal} {...modalProps} />}
    </>
  );
}
